import { toast } from "@/components/ui/use-toast";
import {
  useLazyExportCashLossesQuery,
  useLazyExportConsolidatedLossesQuery,
  useLazyExportConsolidatedPayrollQuery,
  useLazyExportPayrollDiscountsQuery,
} from "@/services/operational-export";
import { useLazyExportPayrollRangeQuery } from "@/services/payroll-voucher";
import type { BlobDownload } from "@/utils/blob-response-handler";
import { downloadFromBlob } from "@/utils/download-blob";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";
import { useCallback } from "react";
import {
  ConsolidatedExportItem,
  ExportMenuItem,
} from "../components/export-tables-menu";
import { EXPORT_ITEM_LABELS, FISCAL_CYCLE_END_DAY } from "../constants";
import { DASHBOARD_LABELS, DASHBOARD_TOASTS } from "../messages";
import { DateRange } from "../types";

export interface DashboardExports {
  rangeItems: readonly ExportMenuItem[];
  consolidatedItems: readonly ConsolidatedExportItem[];
}

const isDownload = (result: unknown): result is BlobDownload =>
  !!result && typeof result === "object" && "blob" in result;

const referenceOf = (year: number, month: number): string =>
  `${year}-${String(month).padStart(2, "0")}-${FISCAL_CYCLE_END_DAY}`;

export const useDashboardExports = (range: DateRange): DashboardExports => {
  const [triggerLosses] = useLazyExportCashLossesQuery();
  const [triggerPayroll] = useLazyExportPayrollDiscountsQuery();
  const [triggerConsolidatedLosses] = useLazyExportConsolidatedLossesQuery();
  const [triggerConsolidatedPayroll] = useLazyExportConsolidatedPayrollQuery();
  const [triggerSeniorSheet] = useLazyExportPayrollRangeQuery();

  const run = useCallback(
    (request: () => Promise<unknown>, label: string): void => {
      toast(DASHBOARD_TOASTS.EXPORT_REQUESTED(label));

      void (async (): Promise<void> => {
        try {
          const result: unknown = await request();

          if (!isDownload(result)) {
            toast(DASHBOARD_TOASTS.EXPORT_EMPTY(label));
            return;
          }

          downloadFromBlob(result.blob, result.filename);
        } catch (error: unknown) {
          toast(
            DASHBOARD_TOASTS.EXPORT_FAILED(
              extractRtkErrorMessage(error, DASHBOARD_LABELS.EXPORT_FALLBACK)
            )
          );
        }
      })();
    },
    []
  );

  return {
    rangeItems: [
      {
        key: "cash-losses",
        label: EXPORT_ITEM_LABELS.CASH_LOSSES,
        onSelect: (): void =>
          run(
            () => triggerLosses(range).unwrap(),
            EXPORT_ITEM_LABELS.CASH_LOSSES
          ),
      },
      {
        key: "payroll-discounts",
        label: EXPORT_ITEM_LABELS.PAYROLL_DISCOUNTS,
        onSelect: (): void =>
          run(
            () => triggerPayroll(range).unwrap(),
            EXPORT_ITEM_LABELS.PAYROLL_DISCOUNTS
          ),
      },
      {
        key: "payroll-senior-sheet",
        label: EXPORT_ITEM_LABELS.PAYROLL_SENIOR_SHEET,
        onSelect: (): void =>
          run(
            () => triggerSeniorSheet(range).unwrap(),
            EXPORT_ITEM_LABELS.PAYROLL_SENIOR_SHEET
          ),
      },
    ],
    consolidatedItems: [
      {
        key: "consolidated-losses",
        label: EXPORT_ITEM_LABELS.CONSOLIDATED_LOSSES,
        onSelect: (year: number, month: number): void =>
          run(
            () =>
              triggerConsolidatedLosses({
                reference: referenceOf(year, month),
              }).unwrap(),
            EXPORT_ITEM_LABELS.CONSOLIDATED_LOSSES
          ),
      },
      {
        key: "consolidated-payroll",
        label: EXPORT_ITEM_LABELS.CONSOLIDATED_PAYROLL,
        onSelect: (year: number, month: number): void =>
          run(
            () =>
              triggerConsolidatedPayroll({
                reference: referenceOf(year, month),
              }).unwrap(),
            EXPORT_ITEM_LABELS.CONSOLIDATED_PAYROLL
          ),
      },
    ],
  };
};
